import { IonButton, IonIcon } from '@ionic/react';
import { callOutline, navigateOutline, openOutline } from 'ionicons/icons';
import { Browser } from '@capacitor/browser';
import React, { useEffect } from 'react';

import CCPage from '../../../components/CCPage';
import { useStoreActions, useStoreState } from '../../../store';
import './storm-pages.css';

const formatUpdated = (iso: string | null): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

const StormEvacPage: React.FC = () => {
  const entry = useStoreState((s) => s.storm.content.evac);
  const locale = useStoreState((s) => s.translations.language);
  const loadContent = useStoreActions((a) => a.storm.loadContent);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const isSpanish = (locale || '').startsWith('es');
  const body = entry ? (isSpanish ? entry.body_es : entry.body_en) : null;
  const updatedAt = entry?.updated_at || null;

  const content = (
    <div className="storm-page-content">
      {updatedAt && (
        <div className="storm-updated-label">
          {isSpanish ? 'Actualizado: ' : 'Last updated: '}{formatUpdated(updatedAt)}
        </div>
      )}

      {!entry && (
        <div className="storm-empty">
          {isSpanish ? 'Cargando contenido...' : 'Loading content...'}
        </div>
      )}

      {body && (
        <>
          {body.intro && (
            <div
              className="storm-page-html"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: body.intro }}
            />
          )}

          {body.map_link && (
            <IonButton
              className="storm-action-button"
              expand="block"
              color="primary"
              onClick={() => { Browser.open({ url: body.map_link }); }}
            >
              <IonIcon icon={navigateOutline} slot="start" />
              {isSpanish ? 'Ver mapa de evacuación' : 'Open evacuation map'}
              <IonIcon icon={openOutline} slot="end" />
            </IonButton>
          )}

          {body.zones && body.zones.length > 0 && (
            <div className="storm-section">
              <h2 className="storm-section-title">
                {isSpanish ? 'Zonas de evacuación' : 'Evacuation zones'}
              </h2>
              {body.zones.map((zone, i) => (
                <div className="storm-card" key={i}>
                  <div className="storm-card-title">
                    {zone.id && <span className="storm-zone-id">{zone.id}</span>}
                    {zone.name}
                  </div>
                  {zone.action && (
                    <div className="storm-action">{zone.action}</div>
                  )}
                  {zone.description && (
                    <div
                      className="storm-page-html"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: zone.description }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {body.shelters && body.shelters.length > 0 && (
            <div className="storm-section">
              <h2 className="storm-section-title">
                {isSpanish ? 'Refugios abiertos' : 'Open shelters'}
              </h2>
              {body.shelters.map((shelter, i) => (
                <div className="storm-card" key={i}>
                  <div className="storm-card-title">{shelter.name}</div>
                  {shelter.address && (
                    <div className="storm-card-meta">
                      <span className="label">{isSpanish ? 'Dirección:' : 'Address:'}</span> {shelter.address}
                    </div>
                  )}
                  {shelter.hours && (
                    <div className="storm-card-meta">
                      <span className="label">{isSpanish ? 'Horario:' : 'Hours:'}</span> {shelter.hours}
                    </div>
                  )}
                  {shelter.phone && (
                    <div className="storm-card-meta">
                      <IonIcon icon={callOutline} style={{ verticalAlign: 'middle' }} />{' '}
                      <a href={`tel:${shelter.phone}`}>{shelter.phone}</a>
                    </div>
                  )}
                  {shelter.notes && (
                    <div
                      className="storm-page-html"
                      style={{ marginTop: 6 }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: shelter.notes }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <CCPage
      title={isSpanish ? 'Rutas de evacuación' : 'Evacuation Routes'}
      content={content}
    />
  );
};

export default StormEvacPage;
